import Alert from 'antd/es/alert';
import Collapse from 'antd/es/collapse';
import Form from 'antd/es/form';
import type { FormInstance } from 'antd/es/form';
import React from 'react';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

import { FormField } from './FormField';
import { getFieldKey, getSchemaType, isFieldRequired } from './utils';

import { logger } from '@utils/logger';

const { Panel } = Collapse;

const FormContainer = styled.div`
  ${({ theme }) => `
  .ant-form-item {
    margin-bottom: ${theme.marginMD}px;
  }

  .ant-form-item:last-child {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    font-weight: ${theme.fontWeightStrong}px;
  }

  .field-description {
    color: ${theme.colorTextSecondary};
    font-size: ${theme.fontSizeSM}px;
    margin-top: ${theme.marginXXS}px;
  }
`}
`;

const SectionContainer = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginLG}px;
  padding: ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border-radius: ${theme.borderRadius}px;

  &:last-child {
    margin-bottom: 0;
  }
`}
`;

const SectionTitle = styled.h4`
  ${({ theme }) => `
  margin: 0 0 ${theme.marginMD}px 0;
  font-size: ${theme.fontSize}px;
  font-weight: ${theme.fontWeightStrong};
  color: ${theme.colorText};
`}
`;

export interface JsonSchemaFormProps {
  schema: JSONSchemaType;
  form: FormInstance;
  name?: string | string[];
  disabled?: boolean;
  readonly?: boolean;
  hideOptional?: boolean;
  layout?: 'horizontal' | 'vertical' | 'inline';
  labelCol?: { span: number };
  wrapperCol?: { span: number };
  onChange?: (values: unknown) => void;
  customComponents?: Record<
    string,
    React.ComponentType<{
      schema: JSONSchemaType;
      disabled?: boolean;
      readonly?: boolean;
      value?: unknown;
      onChange?: (value: unknown) => void;
    }>
  >;
  showDescription?: boolean;
  requiredMark?: boolean | 'optional';
  sectionize?: boolean;
  collapsible?: boolean;
}

export const JsonSchemaForm: React.FC<JsonSchemaFormProps> = ({
  schema,
  form,
  name = [],
  disabled = false,
  readonly = false,
  hideOptional = false,
  layout = 'vertical',
  labelCol,
  wrapperCol,
  onChange,
  customComponents = {},
  showDescription = true,
  requiredMark: _requiredMark = true,
  sectionize = false,
  collapsible = false,
}) => {
  const handleFieldChange = () => {
    if (onChange) {
      // Get all form values and extract the relevant nested values
      const allValues = form.getFieldsValue();
      const relevantValues =
        Array.isArray(name) && name.length > 0
          ? name.reduce((acc, key) => acc?.[key], allValues)
          : allValues;
      onChange(relevantValues);
    }
  };

  const renderField = (
    fieldSchema: JSONSchemaType,
    fieldName: string,
    parentName: string | string[] = [],
    parentRequired: string[] = [],
  ) => {
    let fullName: string | string[];

    if (Array.isArray(parentName)) {
      // Filter out empty strings
      const filteredParent = parentName.filter((n) => n !== '');
      if (filteredParent.length === 0) {
        // If parent is empty array, just use fieldName as string
        fullName = fieldName;
      } else {
        // Otherwise create nested path
        fullName = [...filteredParent, fieldName];
      }
    } else if (parentName) {
      fullName = [parentName, fieldName];
    } else {
      // If parentName is empty, just use fieldName
      fullName = fieldName;
    }

    const isRequired = isFieldRequired(fieldName, parentRequired);

    logger.debug(
      {
        fieldName,
        parentName,
        fullName,
        isRequired,
        fieldSchema,
      },
      'Rendering field',
    );

    // Skip optional fields if hideOptional is true
    if (hideOptional && !isRequired) {
      return null;
    }

    return (
      <FormField
        key={getFieldKey(fullName)}
        schema={fieldSchema}
        name={fullName}
        fieldName={fieldName}
        required={isRequired}
        disabled={disabled}
        readonly={readonly}
        form={form}
        onChange={handleFieldChange}
        renderField={renderField}
        layout={layout}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        customComponents={customComponents}
        showDescription={showDescription}
      />
    );
  };

  const renderObjectProperties = (
    properties: Record<string, JSONSchemaType>,
    required: string[] = [],
  ) => {
    // Group properties into sections if sectionize is enabled
    if (sectionize) {
      const sections: Record<string, [string, JSONSchemaType][]> = {
        required: [],
        optional: [],
      };

      Object.entries(properties).forEach(([key, value]) => {
        if (isFieldRequired(key, required)) {
          sections.required.push([key, value]);
        } else {
          sections.optional.push([key, value]);
        }
      });

      return (
        <>
          {sections.required.length > 0 && (
            <SectionContainer>
              <SectionTitle>Required Fields</SectionTitle>
              {sections.required.map(([key, value]) =>
                renderField(value, key, name, required),
              )}
            </SectionContainer>
          )}
          {sections.optional.length > 0 && !hideOptional && (
            <SectionContainer>
              <SectionTitle>Optional Fields</SectionTitle>
              {sections.optional.map(([key, value]) =>
                renderField(value, key, name, required),
              )}
            </SectionContainer>
          )}
        </>
      );
    }

    // Regular rendering without sections
    return Object.entries(properties).map(([key, value]) => {
      return renderField(value, key, name, required);
    });
  };

  // Handle validation errors
  if (!schema || typeof schema !== 'object') {
    return (
      <Alert
        message="Invalid Schema"
        description="The provided JSON Schema is invalid or missing."
        type="error"
        showIcon
      />
    );
  }

  const schemaType = getSchemaType(schema);

  // Handle root-level primitive types
  if (schemaType !== 'object' || !schema.properties) {
    return (
      <FormContainer>
        <FormField
          schema={schema}
          name={Array.isArray(name) ? [...name, 'value'] : [name, 'value']}
          fieldName="value"
          required={true}
          disabled={disabled}
          readonly={readonly}
          form={form}
          onChange={handleFieldChange}
          renderField={renderField}
          layout={layout}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          customComponents={customComponents}
          showDescription={showDescription}
        />
      </FormContainer>
    );
  }

  // Handle object with properties
  const content = renderObjectProperties(schema.properties, schema.required);

  if (collapsible) {
    return (
      <FormContainer>
        <Form.Item noStyle>
          <Collapse defaultActiveKey={['main']} ghost>
            <Panel header={schema.title || 'Form Fields'} key="main">
              {content}
            </Panel>
          </Collapse>
        </Form.Item>
      </FormContainer>
    );
  }

  return <FormContainer>{content}</FormContainer>;
};

export default JsonSchemaForm;
